'use client'

// Animation
import { motion } from 'framer-motion'

// design-studio
import {
  Body,
  Container,
  fadeInUp,
  H2,
  H3,
  ImandraUniverseLogo,
  MotionWords,
  Pretitle,
  Section,
  staggerContainer,
} from 'design-studio'

// Data
import { imandraUniverseTiers } from 'design-studio/data/pricing'

// Utils
import { Join } from 'design-studio/lib'

export function SectionPricing() {
  return (
    <Section
      id="pricing"
      className="relative z-10 overflow-hidden py-16 md:py-24 lg:py-32"
    >
      <Container className="relative z-10 max-w-6xl">
        <div
          className={Join(
            'overflow-hidden rounded-2xl border border-slate-300 bg-slate-50/80 backdrop-blur-md',
            'p-6 md:p-10 xl:p-20',
            'dark:border-slate-500/60 dark:bg-slate-950/50',
          )}
        >
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            className="space-y-10"
          >
            <motion.div
              variants={fadeInUp}
              className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-x-24"
            >
              <div className="min-w-0">
                <Pretitle
                  variant="sans"
                  font="display"
                  tone="accent"
                  className="mb-6 block text-teal-600! dark:text-teal-500!"
                >
                  Pricing
                </Pretitle>
                <H2 className="mb-3 max-w-[18ch]">
                  <MotionWords>Available plans</MotionWords>
                </H2>
                <Body
                  size="md"
                  tone="muted"
                  className="max-w-[60ch] font-light"
                >
                  Select a plan that scales with your automated reasoning needs,
                  from building AI agents with reasoning skills to deploying
                  enterprise-grade formal verification.
                </Body>
              </div>
              <div className="flex min-w-0 flex-col items-start gap-6 md:justify-start">
                <ImandraUniverseLogo
                  className={Join(
                    'mt-0 h-8 min-w-24 shrink-0 text-slate-800',
                    'md:mt-12 md:h-12 md:min-w-36 dark:text-slate-100',
                  )}
                />
                <Body size="md" tone="muted" className="max-w-[60ch] font-bold">
                  Imandra Universe is the ecosystem through which ImandraX,
                  CodeLogician, and other reasoning tools are delivered.
                </Body>
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="mb-0 grid grid-cols-1 divide-y divide-slate-300 border-t border-slate-300 lg:grid-cols-3 lg:items-start lg:divide-x lg:divide-y-0 dark:divide-slate-500/80 dark:border-slate-500/80"
            >
              {imandraUniverseTiers.map((tier) => (
                <div
                  key={tier.name}
                  className="relative z-20 px-8 py-8 lg:px-10"
                >
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <H3 size="sm" font="sans">
                      {tier.name}
                    </H3>
                    <div className="flex items-center gap-2">
                      {tier.trialNote ? (
                        <span className="rounded-full bg-teal-500/20 px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-teal-600 ring-1 ring-teal-500/40 dark:text-teal-400">
                          {tier.trialNote}
                        </span>
                      ) : null}
                      {tier.mostPopular ? (
                        <span className="rounded-full bg-indigo-500 px-2.5 py-1 text-xs font-semibold whitespace-nowrap text-white">
                          Most Popular
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <Body
                    size="sm"
                    tone="subtle"
                    font="sans"
                    className="mb-4 text-left font-light"
                  >
                    {tier.description}
                  </Body>
                  <div className="flex items-baseline gap-x-1">
                    <span className="text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">
                      {tier.price}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {tier.priceNote}
                    </span>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex justify-center border-t border-slate-300 pt-8 dark:border-slate-500/80"
            >
              <a
                href="https://universe.imandra.ai/#pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex rounded-full bg-teal-600 px-10 py-3 text-sm font-medium text-white transition-colors hover:bg-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-white focus:outline-none dark:focus:ring-offset-cyan-950"
              >
                Learn more and start
              </a>
            </motion.div>
          </motion.div>
        </div>
      </Container>
    </Section>
  )
}
